import { Alert, Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, Paper, Stack, TextField, Typography, useMediaQuery, useTheme } from "@mui/material"
import { useState, useEffect, useContext, useReducer, useCallback, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import useInternalFetch from "../../api/apiInternal"

import TransferList from "../../components/TransferList"
import FilterClauses from "./components/FilterClauses"
import AuthContext from "../../context/AuthProvider"
import Loading from "../../components/Loading"
import GeneralError from "../../components/GeneralError"
import { convertDbNumberToHtml } from "../../common/Helper"
import config from "../../common/Config"
import { filterPublicFields } from "../../common/Fields"
import RestrictedAccess from "../../components/RestrictedAccess";

const TabFilterSettings = (props) => {
	const [, forceUpdate] = useReducer(x => x + 1, 0);

	const internalFetch = useInternalFetch()
	const [screenerId, setScreenerId] = useState(props.screenerId)
	const { auth } = useContext(AuthContext)
	const location = useLocation();

	const [fields, setFields]									= useState()
	const [selectedFields, setSelectedFields]					= useState([])
	const [filterClauses, setFilterClauses]						= useState([{field: '', operation: '', parameter: '', isValid: 0}])
	
	const [title, setTitle]										= useState('')
	const [author, setAuthor]									= useState('')
	const [description, setDescription]							= useState('')
	const [convertToDollar, setConvertToDollar]					= useState(false)
			
	const isFirstValidation										= useRef(true)
	const [isLoading, setIsLoading]								= useState(true)
	const [isSaving, setIsSaving]								= useState(false)

	const [hasLoadingErrors, setHasLoadingErrors]				= useState(false)
	const [formState, setFormState]								= useState(formFields)
	const [returnMessage, setReturnMessage]						= useState()

	const theme 												= useTheme()
	const isSmallScreen 										= useMediaQuery(theme.breakpoints.down('sm'))

	useEffect(() => {

		if (!auth.userId) {
			setIsLoading(false)
			return
		}

		internalFetch(config.apiUrl + '/api/screener/fields')
		.then(data => {
			if (!data.status) {
				const filteredCategories = filterPublicFields(data)
				setFields(filteredCategories.sort((v1, v2) => v1.categoryOrder !== v2.categoryOrder ? v1.categoryOrder - v2.categoryOrder : v1.label.localeCompare(v2.label)))
			}
			
		})
		if (screenerId > 0) {
			internalFetch(config.apiUrl + '/api/screener/' + screenerId)
			.then(data => {
				if (!data.status) {
					setTitle(data.title)
					setAuthor(screenerId === 0 ? auth.user : (data.user?.username || ''))
					setDescription(data.description)
					setConvertToDollar(data.convertToDollar)
					setSelectedFields(data.fields.sort((a, b) => a.screenerFields.fieldOrder - b.screenerFields.fieldOrder).map(f => f.id))
					setFilterClauses(data.screenerParameters.map(p => ({ ...p, parameter: convertDbNumberToHtml(p.parameter), field: p.fieldId, isValid: true })))
					setIsLoading(false)
					setHasLoadingErrors(false)
				}
				else {
					setIsLoading(false)
					setHasLoadingErrors(true)
				}
			})
		}
		else {
			setAuthor(auth.user)
			setIsLoading(false)
		}
	}, [auth.user, screenerId])

	useEffect(() => {
		if (!isFirstValidation.current) checkValidity()
	}, [title, filterClauses])

	const handleSelectFields = useCallback((value) => {
		setSelectedFields(value)
	}, [])

	const handleSaveFilter = () => {
		saveFilter(false)	
	}

	const handleCopyFilter = () => {
		saveFilter(true)
	}

	const handleReturnToList = (id) => {
		if (props.onSaveSucess) props.onSaveSucess()
	}

	const saveFilter = (isCopying = false) => {
	if (checkValidity()) {
			setIsSaving(true)

			internalFetch(config.apiUrl +'/api/screener', {
				method : 'POST',
				headers : {
					'content-type' : 'application/json; charset=utf-8',
				},
				body : JSON.stringify({
					id				: (isCopying ? 0 : screenerId), 
					title 			: (isCopying ? 'Copia de ' : '') + title,
					author			: author,
					description		: description,
					convertToDollar	: convertToDollar,
					fields 			: selectedFields,
					parameters 		: filterClauses
				})
			})
			.then(data => {
				switch (data.status) {
					case 400 : 
						setReturnMessage({
							severity 	: 'error',
							message		: 'Erro ao salvar filtro: filtro não encontrado.'
						})					
						break;
					case 403 :
						setReturnMessage({
							severity 	: 'error',
							message		: 'Erro ao salvar filtro: permissão negada.'
						})					
						break;						 
					case 422 : 
						setReturnMessage({
							severity 	: 'error',
							message		: 'Erro ao salvar filtro: limite de filtros excedidos para este usuário.'
						})	
						break;
					case 500 : 
						setReturnMessage({
							severity 	: 'error',
							message		: 'Erro ao salvar filtro.'
						})
						break;
					default : 
						setReturnMessage({
							severity 	: 'success',
							message		: <Typography>Filtro salvo com sucesso! Clique <Link to={'/stockscreener/'+ data.id} onClick={() => handleReturnToList(data.id)}>aqui</Link> para os resultados</Typography>
						})
						setScreenerId(data.id)
						break;
				}

				setIsSaving(false)
			})
		}		
	}

	const checkValidity = () => {
		let isValid = true

		isFirstValidation.current 		= false

		// Reseta mensagens de erro
		formFields.title.error 			= false
		formFields.filterClauses.error 	= false

		// Verifica se foi preenchid um titulo
		if (title.length < 1) {
			formFields.title.error = true
			isValid = false
		}

		// Verifica se ao menos um parâmetro foi criado corretamente
		if (!filterClauses[0] || filterClauses[0].field === '' || filterClauses[0].operation === '' || filterClauses[0].parameter === '' ) {
			formFields.filterClauses.error = true
			formFields.filterClauses.errorMessage = 'Você deve definir ao menos um filtro.'
			isValid = false
		}
		// Se houver mais de um parâmetro, verifica se todos estão criados corretamente
		else if (filterClauses.length > 1) {
			filterClauses.map(fc => {
				if (!fc.isValid) {
					formFields.filterClauses.error = true
					formFields.filterClauses.errorMessage = 'Um ou mais mais parâmetros não preenchidos corretamente.'
					isValid = false
				}
			})
		}

		setFormState(formFields)
		forceUpdate()

		return isValid
	}

	if (isSmallScreen) return <GeneralError message="A configuração dos parâmetros do screener ainda não está otimizada para telas pequenas."/>

	if (isLoading) return <Loading/>

	if (hasLoadingErrors) return <GeneralError message="Erro ao carregar filtro"/>

	if (!auth.userId) return (<Box height={'100%'} width={'100%'} display="flex" justifyContent="center" alignItems="center"><RestrictedAccess message="Faça login para configurar este filtro."/></Box>)
	

	return (
		<Box>
			{returnMessage ? <Alert severity={returnMessage.severity} onClose={() => setReturnMessage(null)}>{returnMessage.message}</Alert> : null }
			<Grid container spacing={1} marginBottom={1}>
				<Grid item xs={12} lg={6} justifyContent={'center'}>
					<Stack direction='column' spacing={1} marginTop={1}>
						<Paper>
							<Stack direction='column' spacing={1} marginTop={1} padding={1}>
								<TextField 
									name="title" 
									label="Título" 
									value={title} 
									onChange={(e) => setTitle(e.target.value)}
									error={formState.title.error}
									helperText={formState.title.error && formState.title.errorMessage}	
									inputProps={{ maxLength: 50 }}		
								/>
								<TextField name="author" label="Autor" value={author} disabled/>
								<FormGroup>
									<FormControlLabel control={
										<Checkbox checked={convertToDollar} onChange={() => setConvertToDollar(!convertToDollar)} />
									} label="Converter Dados Financeiros para Dólar" />
								</FormGroup>
								<Box
									component={Link}
									to="/user/settings"
									target="_blank"
									sx={{
										display: "inline-flex",
										alignItems: "center",
										gap: 0.75,
										color: "text.primary",
										textDecoration: "underline",
										textDecorationStyle: "dotted",
										textUnderlineOffset: 4,
										fontSize: "0.9rem"
									}}
								>
									<OpenInNewIcon fontSize="small" sx={{ color: "text.secondary" }} />
									<Typography ml={0.7}>Ajustar Definições de Risco e Crescimento</Typography>
								</Box>
							</Stack>
						</Paper>
						<Paper width={'100%'}>
							<Stack direction='column' spacing={1} marginTop={1} padding={1}>
								<Typography variant="h6">Colunas</Typography>
								<Box display={'flex'} justifyContent={'left'}>
									<TransferList options={fields} selectedOptions={selectedFields} onChange={handleSelectFields}/>
								</Box>
							</Stack>
						</Paper>
					</Stack>
				</Grid>

				<Grid item xs={12} lg={6} justifyContent={'center'}>
					<FilterClauses
						fields={fields}
						filterClauses={filterClauses}
						onChange={setFilterClauses}
						error={formState.filterClauses.error}
						helperText={formState.filterClauses.error && formState.filterClauses.errorMessage}				
					/>
				</Grid>
			</Grid>
			<Box display="flex" justifyContent="right" marginBottom={2} marginTop={1}>
				<Button variant='contained' onClick={(auth.user !== author && screenerId > 0) ? handleCopyFilter : handleSaveFilter} {...isSaving ? {disabled: true} : null} >
					{isSaving ? 'Salvando... ' : (auth.user !== author && screenerId > 0) ? 'Salvar Cópia' : 'Salvar'}
				</Button>
			</Box>

		</Box>
	)
}

const formFields = {
	title : {
		error 			: false,
		errorMessage	: 'Digite um título para seu filtro.'
	},
	filterClauses : {
		error			: false,
		errorMessage	: 'Você deve definir ao menos um filtro.'
	}
}

export default TabFilterSettings
